import { useEffect } from 'react';
import {
  Actions,
  Async,
  FSpread,
  toAsyncFetched,
} from '@client/new/helpers/types';
import { useSubscriptionBundlesModel } from '../model/subscriptionBundles.model';
import {
  BundlesCards,
  toHelpMixpanelEvent,
  HelpEventType,
  toBundleCards,
} from './subscriptionBundle.vm.mapper';
import { EditBundlePriceVM, useEditBundlePriceVM } from './editBundlePrice.vm';
import {
  EditBundleContentVM,
  useEditBundleContentVM,
} from './editBundleContent.vm';
import {
  EditPlatformBundleVM,
  useEditPlatformBundleVM,
} from './editPlatformBundle.vm';
import { MigrateToBundleVM, useMigrateToBundleVM } from './migrateToBundle.vm';
import { isEmpty } from 'lodash';
import { CreateBundleVM, useCreateBundleVM } from './createBundle.vm';
import { BundlePricing } from 'generated/graphql';
import { getHash } from '../../common/helpers';

export type BundlesVMData = {
  hideSubscriptionBundles: boolean;
  showEmptyState: boolean;
  showMigrateToBundle: boolean;
  cards: BundlesCards;
  emptyState: {
    create: () => void;
  };
};

type PayloadMap = {
  helpMaterialsEvent: HelpEventType;
};

export type SubscriptionBundleVMAction = Actions<PayloadMap>;
export type SubscriptionBundleVMDispatcher = FSpread<PayloadMap>;

export type SubscriptionBundlesVM = BundlesVMData &
  Pick<
    EditBundlePriceVM,
    | 'confirmPriceModal'
    | 'priceEditModal'
    | 'confirmPriceDisableModal'
    | 'confirmPublishModal'
    | 'linksModal'
  > &
  Pick<EditBundleContentVM, 'list' | 'confirmSaveModal'> &
  Pick<EditPlatformBundleVM, 'platformBundleModal' | 'pushNotificationModal'> &
  Pick<CreateBundleVM, 'createBundleModal'> &
  MigrateToBundleVM & {
    dispatch: SubscriptionBundleVMDispatcher;
  };

export const useSubscriptionBundlesVM = ({
  creatorId,
}: {
  creatorId: number;
}): Async<SubscriptionBundlesVM> => {
  const paramBundleId = getHash();
  const model = useSubscriptionBundlesModel({ creatorId });
  const {
    priceEditModal,
    confirmPriceModal,
    confirmPriceDisableModal,
    confirmPublishModal,
    editBundlePrice,
    submittingBundleIds,
    linksModal,
    openLinksModal,
  } = useEditBundlePriceVM({
    model,
    creatorId,
  });
  const { list, confirmSaveModal } = useEditBundleContentVM({ model });
  const { editPlatformBundle, platformBundleModal, pushNotificationModal } =
    useEditPlatformBundleVM({ model });
  const { migrateToBundle } = useMigrateToBundleVM({ creatorId, model });
  const refetch = model.type === 'fetched' ? model.value.refetch : undefined;
  const { createBundle, createBundleModal } = useCreateBundleVM({
    creatorId,
    refetch,
  });

  useEffect(() => {
    if (paramBundleId && !isNaN(Number(paramBundleId))) {
      if (model.type === 'fetched') {
        const bundleFromParam = model.value.initialSubscriptionBundles.find(
          (b) =>
            b.id === Number(paramBundleId) &&
            b.pricing === BundlePricing.Subscription,
        );
        if (bundleFromParam) {
          bundleFromParam.isPlatformBundle
            ? editPlatformBundle(bundleFromParam)
            : editBundlePrice(bundleFromParam);
        }
      }
    }
  }, [model.type]);

  if (model.type !== 'fetched') return model;

  const dispatch: SubscriptionBundleVMDispatcher = (type, ...args) => {
    const action = { type, payload: args[0] } as SubscriptionBundleVMAction;
    switch (action.type) {
      case 'helpMaterialsEvent':
        toHelpMixpanelEvent(action.payload);
        return null;
    }
  };

  return toAsyncFetched<SubscriptionBundlesVM>({
    dispatch,
    hideSubscriptionBundles: model.value.hideSubscriptionBundles,
    showEmptyState: isEmpty(model.value.initialSubscriptionBundles),
    showMigrateToBundle: !model.value.bundleBusinessModel,
    cards: toBundleCards(
      model.value.initialSubscriptionBundles,
      editBundlePrice,
      editPlatformBundle,
      submittingBundleIds,
      openLinksModal,
    ),
    list,
    confirmSaveModal,
    priceEditModal,
    confirmPriceModal,
    confirmPriceDisableModal,
    confirmPublishModal,
    emptyState: {
      create: createBundle,
    },
    platformBundleModal,
    pushNotificationModal,
    migrateToBundle,
    createBundleModal,
    linksModal,
  });
};
